import { ComponentType } from 'react';
import type { ViewContext } from '../pages/StubPage';

// Existing platform pages (eagerly imported for backward compatibility)
import DashboardPage from '../pages/DashboardPage';
import AdminUsersPage from '../pages/AdminUsersPage';
import RolesPage from '../pages/RolesPage';
import RequestsPage from '../pages/RequestsPage';
import LogsPage from '../pages/LogsPage';
import EmailPage from '../pages/EmailPage';
import SSOPage from '../pages/SSOPage';
import APIPage from '../pages/APIPage';
import LLMPage from '../pages/LLMPage';
import AICommandCenterPage from '../pages/AICommandCenterPage';
import BICommandCenterPage from '../pages/BICommandCenterPage';
import DocsPage from '../pages/DocsPage';
import SchemaPage from '../pages/SchemaPage';
import SettingsPage from '../pages/SettingsPage';
import TenantsPage from '../pages/TenantsPage';
import ProfilePage from '../pages/ProfilePage';
import UsageDashboardPage from '../pages/UsageDashboardPage';
import UpgradesPage from '../pages/UpgradesPage';
import ContainersPage from '../pages/ContainersPage';
import TenantSettingsPage from '../pages/TenantSettingsPage';
import TenantUsageDashboardPage from '../pages/TenantUsageDashboardPage';
import TenantBackupsPage from '../pages/TenantBackupsPage';
import PlatformBackupsPage from '../pages/PlatformBackupsPage';
import TemplatesPage from '../pages/TemplatesPage';
import { ExecOverview } from '../pages/iwt';
import AdjacencyMapPage from '../pages/iwt/AdjacencyMapPage';
import IntegrationMapPage from '../pages/iwt/IntegrationMapPage';
import DemoCenterPage from '../pages/iwt/DemoCenterPage';
import CloudOpsPage from '../pages/CloudOpsPage';
import MFASetupPage from '../pages/MFASetupPage';
import TenantAdminDashboard from '../features/tenants/components/TenantAdminDashboard';
import TenantUserLanding from '../features/tenants/components/TenantUserLanding';
import FunctionalAreaDashboardPage from '../pages/FunctionalAreaDashboardPage';
import FeaturesPage from '../pages/FeaturesPage';
import AIPoliciesPage from '../pages/AIPoliciesPage';
import NotificationCenterPage from '../pages/NotificationCenterPage';
import AllocationsPage from '../pages/AllocationsPage';
import MyCapabilitiesPage from '../pages/MyCapabilitiesPage';
import SkillCommissioningPage from '../pages/SkillCommissioningPage';
import SecurityPage from '../pages/SecurityPage';
import WorkItemsPage from '../pages/WorkItemsPage';
import RagCollectionsPage from '../pages/RagCollectionsPage';
import StubPage from '../pages/StubPage';

// Re-export ViewContext for consumers
export type { ViewContext };

type ViewComponent = ComponentType<{ context?: ViewContext }>;

/**
 * Central view registry mapping view keys to React components.
 *
 * Platform admin views use their tab IDs as keys (backward compatible).
 * Template-driven views use the nav item's `view` field as key.
 * Unknown keys fall back to StubPage.
 */
const VIEW_MAP: Record<string, ViewComponent> = {
  // Platform admin views
  'dashboard': DashboardPage as ViewComponent,
  'users': AdminUsersPage as ViewComponent,
  'roles': RolesPage as ViewComponent,
  'requests': RequestsPage as ViewComponent,
  'logs': LogsPage as ViewComponent,
  'email': EmailPage as ViewComponent,
  'sso': SSOPage as ViewComponent,
  'api': APIPage as ViewComponent,
  'llm': LLMPage as ViewComponent,
  'ai-command-center': AICommandCenterPage as ViewComponent,
  'bi-command-center': BICommandCenterPage as ViewComponent,
  'docs': DocsPage as ViewComponent,
  'schema': SchemaPage as ViewComponent,
  'settings': SettingsPage as ViewComponent,
  'tenants': TenantsPage as ViewComponent,
  'profile': ProfilePage as ViewComponent,
  'usage': UsageDashboardPage as ViewComponent,
  'upgrades': UpgradesPage as ViewComponent,
  'containers': ContainersPage as ViewComponent,
  'tenant-settings': TenantSettingsPage as ViewComponent,
  'tenant-usage': TenantUsageDashboardPage as ViewComponent,
  'tenant-backups': TenantBackupsPage as ViewComponent,
  'platform-backups': PlatformBackupsPage as ViewComponent,
  'cloud-ops': CloudOpsPage as ViewComponent,
  'templates': TemplatesPage as ViewComponent,
  'features': FeaturesPage as ViewComponent,
  'ai-policies': AIPoliciesPage as ViewComponent,
  'allocations': AllocationsPage as ViewComponent,
  'my-capabilities': MyCapabilitiesPage as ViewComponent,
  'skill-commissioning': SkillCommissioningPage as ViewComponent,

  // Security dashboard (DEF-015 WQ-G — multi-skill-aware Portal surface)
  'security': SecurityPage as ViewComponent,

  // Work Items backlog (WQ-298 — operator surface for ADR-019 REM- items)
  'work-items': WorkItemsPage as ViewComponent,

  // RAG Collections harness-tagging (WQ-311 — collection-level classification)
  'rag-collections': RagCollectionsPage as ViewComponent,

  // IWT views — Executive Views (Phase 4.6.1)
  'exec-overview': ExecOverview as ViewComponent,
  'exec-adjacency': AdjacencyMapPage as ViewComponent,
  'exec-integration': IntegrationMapPage as ViewComponent,

  // IWT views — Demo Center launcher (WQ-337); the item view and the /demo-center path
  'demo-center': DemoCenterPage as ViewComponent,

  // MFA (WQ-067)
  'mfa-setup': MFASetupPage as ViewComponent,

  // Tenant dashboards (WQ-086)
  'tenant-admin-dashboard': TenantAdminDashboard as ViewComponent,
  'tenant-landing': TenantUserLanding as ViewComponent,

  // Functional area dashboards (WQ-087a + WQ-087b)
  // Area keys — used when group header is clicked
  'executive': FunctionalAreaDashboardPage as ViewComponent,
  'finance': FunctionalAreaDashboardPage as ViewComponent,
  'people-culture': FunctionalAreaDashboardPage as ViewComponent,
  'sales-marketing': FunctionalAreaDashboardPage as ViewComponent,
  'operations': FunctionalAreaDashboardPage as ViewComponent,
  'engineering-rd': FunctionalAreaDashboardPage as ViewComponent,
  // WQ-087b: 13 new area keys
  'legal-compliance': FunctionalAreaDashboardPage as ViewComponent,
  'it-infrastructure': FunctionalAreaDashboardPage as ViewComponent,
  'customer-success': FunctionalAreaDashboardPage as ViewComponent,
  'project-management': FunctionalAreaDashboardPage as ViewComponent,
  'supply-chain': FunctionalAreaDashboardPage as ViewComponent,
  'quality-assurance': FunctionalAreaDashboardPage as ViewComponent,
  'data-analytics': FunctionalAreaDashboardPage as ViewComponent,
  'communications': FunctionalAreaDashboardPage as ViewComponent,
  'facilities': FunctionalAreaDashboardPage as ViewComponent,
  'innovation': FunctionalAreaDashboardPage as ViewComponent,
  'product': FunctionalAreaDashboardPage as ViewComponent,
  'bizdev': FunctionalAreaDashboardPage as ViewComponent,
  'partnerships': FunctionalAreaDashboardPage as ViewComponent,
  // Group IDs that map to composite/renamed areas
  'hr': FunctionalAreaDashboardPage as ViewComponent,
  'sales': FunctionalAreaDashboardPage as ViewComponent,
  'marketing': FunctionalAreaDashboardPage as ViewComponent,
  'engineering': FunctionalAreaDashboardPage as ViewComponent,
  'rnd': FunctionalAreaDashboardPage as ViewComponent,
  'program-mgmt': FunctionalAreaDashboardPage as ViewComponent,
  'it': FunctionalAreaDashboardPage as ViewComponent,
  'legal': FunctionalAreaDashboardPage as ViewComponent,
  'quality': FunctionalAreaDashboardPage as ViewComponent,

  // Notification Center (Phase 4.7.5 — WQ-170)
  'admin-notifications': NotificationCenterPage as ViewComponent,
  'notification-center': NotificationCenterPage as ViewComponent,

  // Stub fallback
  'stub': StubPage,
};

/**
 * Resolve a view key to a component.
 * Falls back to StubPage if key not found.
 */
export function resolveView(viewKey: string | undefined): ViewComponent {
  if (!viewKey) return VIEW_MAP['stub'];
  return VIEW_MAP[viewKey] || VIEW_MAP['stub'];
}

/**
 * Register a new view at runtime.
 * Used for dynamically adding tenant-specific views.
 */
export function registerView(key: string, component: ViewComponent): void {
  VIEW_MAP[key] = component;
}

/**
 * Check if a view key has a real implementation (not stub).
 */
export function isViewImplemented(viewKey: string): boolean {
  return viewKey in VIEW_MAP && viewKey !== 'stub';
}

export default VIEW_MAP;
